import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Users, DollarSign, FileText, Award, LogIn, Power, Clock, Trash2, Key, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '/api';

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader),
        axios.get(`${API}/admin/institutions`, authHeader)
      ]);
      setStats(s.data);
      setInstitutions(i.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('admin_token');
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData();
    // eslint-disable-next-line
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('admin_token', res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בהתחברות');
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  const toggleActive = async (id) => {
    await axios.put(`${API}/admin/institutions/${id}/toggle`, {}, authHeader);
    loadData();
  };

  const extendTrial = async (id) => {
    const days = prompt('כמה ימים להאריך?', '30');
    if (!days) return;
    await axios.put(`${API}/admin/institutions/${id}/extend`, { days }, authHeader);
    loadData();
  };

  const changePlan = async (id) => {
    const plan = prompt('תוכנית חדשה (basic / pro / unlimited):', 'pro');
    if (!plan) return;
    await axios.put(`${API}/admin/institutions/${id}/plan`, { plan }, authHeader);
    loadData();
  };

  const resetPassword = async (id) => {
    const pw = prompt('סיסמה חדשה:');
    if (!pw) return;
    await axios.put(`${API}/admin/institutions/${id}/reset-password`, { new_password: pw }, authHeader);
    alert('הסיסמה אופסה');
  };

  const impersonate = async (id) => {
    const res = await axios.post(`${API}/admin/institutions/${id}/impersonate`, {}, authHeader);
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('institution', JSON.stringify(res.data.institution));
    window.location.href = '/dashboard';
  };

  const deleteInst = async (id, name) => {
    if (!window.confirm(`למחוק את "${name}"? פעולה בלתי הפיכה!`)) return;
    await axios.delete(`${API}/admin/institutions/${id}`, authHeader);
    loadData();
  };

  // מסך התחברות
  if (!token) {
    return (
      <div style={{minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#0f172a'}}>
        <div style={{background:'white', borderRadius:16, padding:40, width:'100%', maxWidth:380, boxShadow:'0 10px 40px rgba(0,0,0,0.3)'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8, color:'#0f172a'}}>
            <Shield size={28} />
            <h1 style={{fontSize:22, fontWeight:700}}>פאנל ניהול</h1>
          </div>
          <p style={{textAlign:'center', color:'#64748b', fontSize:14, marginBottom:24}}>גישת מנהל בלבד</p>
          {error && <div style={{background:'#fee2e2', color:'#dc2626', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16, textAlign:'center'}}>{error}</div>}
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="סיסמת מנהל"
              className="input"
              style={{marginBottom:16}}
              autoFocus
            />
            <button type="submit" className="btn btn-primary btn-full" style={{justifyContent:'center'}}>
              <Shield size={16} /> כניסה
            </button>
          </form>
        </div>
      </div>
    );
  }

  const fmt = (d) => d ? new Date(d).toLocaleDateString('he-IL') : '—';
  const trialDays = (inst) => {
    if (inst.plan !== 'trial' || !inst.trial_ends_at) return null;
    const end = new Date(inst.trial_ends_at);
    const now = new Date();
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.max(0, Math.round((endDay - nowDay) / 86400000));
  };

  return (
    <div style={{minHeight:'100vh', background:'var(--bg)', padding:'24px'}}>
      <div style={{maxWidth:1400, margin:'0 auto'}}>
        {/* כותרת */}
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24}}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <Shield size={26} color="var(--primary)" />
            <h1 style={{fontSize:24, fontWeight:700}}>פאנל ניהול</h1>
          </div>
          <div style={{display:'flex', gap:8}}>
            <button className="btn btn-secondary" onClick={loadData}><RefreshCw size={16} /> רענן</button>
            <button className="btn btn-secondary" onClick={logout} style={{color:'var(--danger)'}}>יציאה</button>
          </div>
        </div>

        {/* סטטיסטיקות */}
        {stats && (
          <div className="grid-4" style={{marginBottom:24}}>
            <div className="card">
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <Users size={22} color="var(--primary)" />
                <div>
                  <div style={{fontSize:24, fontWeight:700}}>{stats.institutions.total}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)'}}>מוסדות · {stats.institutions.paying} משלמים · {stats.institutions.trial} בניסיון</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <DollarSign size={22} color="var(--success)" />
                <div>
                  <div style={{fontSize:24, fontWeight:700}}>₪{Number(stats.revenue.total_revenue).toLocaleString()}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)'}}>סה"כ הכנסות · {stats.revenue.total_payments} תשלומים</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <FileText size={22} color="var(--warning)" />
                <div>
                  <div style={{fontSize:24, fontWeight:700}}>{stats.usage.templates}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)'}}>תבניות במערכת</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                <Award size={22} color="var(--primary)" />
                <div>
                  <div style={{fontSize:24, fontWeight:700}}>{stats.usage.certificates}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)'}}>אישורים שהופקו</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* טבלת מוסדות */}
        <div className="card">
          <h2 style={{fontSize:16, fontWeight:600, marginBottom:16}}>מוסדות ({institutions.length})</h2>
          {loading ? <p>טוען...</p> : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:'2px solid var(--border)', textAlign:'right'}}>
                    <th style={{padding:'8px'}}>שם</th>
                    <th style={{padding:'8px'}}>מייל</th>
                    <th style={{padding:'8px'}}>טלפון</th>
                    <th style={{padding:'8px'}}>תוכנית</th>
                    <th style={{padding:'8px'}}>סטטוס</th>
                    <th style={{padding:'8px'}}>תבניות</th>
                    <th style={{padding:'8px'}}>אישורים</th>
                    <th style={{padding:'8px'}}>שילם</th>
                    <th style={{padding:'8px'}}>נרשם</th>
                    <th style={{padding:'8px'}}>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {institutions.map(inst => {
                    const days = trialDays(inst);
                    return (
                      <tr key={inst.id} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'8px', fontWeight:600}}>{inst.name}</td>
                        <td style={{padding:'8px', color:'var(--text-muted)'}}>{inst.email}</td>
                        <td style={{padding:'8px', color:'var(--text-muted)'}} dir="ltr">{inst.phone || '—'}</td>
                        <td style={{padding:'8px'}}>
                          <span className="badge badge-info">{inst.plan}</span>
                        </td>
                        <td style={{padding:'8px'}}>
                          {!inst.is_active ? <span className="badge badge-danger">מושבת</span> :
                           inst.plan === 'trial' ? <span className="badge badge-warning">ניסיון {days}י'</span> :
                           <span className="badge badge-success">פעיל</span>}
                        </td>
                        <td style={{padding:'8px', textAlign:'center'}}>{inst.templates_count}</td>
                        <td style={{padding:'8px', textAlign:'center'}}>{inst.certificates_count}</td>
                        <td style={{padding:'8px'}}>₪{Number(inst.total_paid).toLocaleString()}</td>
                        <td style={{padding:'8px', color:'var(--text-muted)'}}>{fmt(inst.created_at)}</td>
                        <td style={{padding:'8px'}}>
                          <div style={{display:'flex', gap:4}}>
                            <button onClick={() => toggleActive(inst.id)} title="הפעלה/השבתה" style={iconBtn}><Power size={15} /></button>
                            <button onClick={() => extendTrial(inst.id)} title="הארכת ניסיון" style={iconBtn}><Clock size={15} /></button>
                            <button onClick={() => changePlan(inst.id)} title="שינוי תוכנית" style={iconBtn}><RefreshCw size={15} /></button>
                            <button onClick={() => impersonate(inst.id)} title="כניסה כמוסד" style={iconBtn}><LogIn size={15} /></button>
                            <button onClick={() => resetPassword(inst.id)} title="איפוס סיסמה" style={iconBtn}><Key size={15} /></button>
                            <button onClick={() => deleteInst(inst.id, inst.name)} title="מחיקה" style={{...iconBtn, color:'var(--danger)'}}><Trash2 size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const iconBtn = {
  background:'none', border:'1px solid var(--border)', borderRadius:6,
  padding:'5px 7px', cursor:'pointer', color:'var(--text-muted)',
  display:'flex', alignItems:'center'
};
